export const searchCategories = [
  'Platform',
  'Solutions',
  'Industries',
  'Resources',
  'Company',
] as const;
export type SearchCategory = (typeof searchCategories)[number];
export type PublicSearchEntry = {
  path: string;
  title: string;
  name: string;
  description: string;
  keywords: string[];
  category: SearchCategory;
  draft?: boolean;
};
export type SearchMatch = PublicSearchEntry & { score: number };
export function normalizeSearch(value: string) {
  return value.normalize('NFKC').toLocaleLowerCase('en').trim().replace(/\s+/g, ' ');
}
export function rankPublicSearch(
  index: readonly PublicSearchEntry[],
  query: string,
  category: SearchCategory | 'All' = 'All'
): SearchMatch[] {
  const normalized = normalizeSearch(query);
  const tokens = normalized.split(' ').filter(Boolean);
  return index
    .filter((item) => category === 'All' || item.category === category)
    .flatMap((item) => {
      const name = normalizeSearch(item.name),
        title = normalizeSearch(item.title),
        description = normalizeSearch(item.description),
        keywords = normalizeSearch(item.keywords.join(' ')),
        group = normalizeSearch(item.category);
      const haystack = `${name} ${title} ${description} ${keywords} ${group}`;
      if (!tokens.every((token) => haystack.includes(token))) return [];
      const score = !normalized
        ? 0
        : (name === normalized
            ? 160
            : title === normalized
              ? 140
              : name.startsWith(normalized)
                ? 110
                : title.startsWith(normalized)
                  ? 100
                  : name.includes(normalized)
                    ? 85
                    : title.includes(normalized)
                      ? 75
                      : keywords.includes(normalized)
                        ? 50
                        : description.includes(normalized)
                          ? 25
                          : 10) +
          tokens.reduce(
            (n, token) =>
              n +
              (name.includes(token)
                ? 8
                : title.includes(token)
                  ? 6
                  : keywords.includes(token)
                    ? 3
                    : 1),
            0
          );
      return [{ ...item, score }];
    })
    .sort(
      (a, b) => b.score - a.score || a.name.localeCompare(b.name) || a.path.localeCompare(b.path)
    );
}
export const popularSearchPaths = [
  '/platform/employee-management',
  '/platform/payroll',
  '/platform/attendance',
  '/industries',
  '/resources/docs',
  '/request-demo',
] as const;
export function publicSearchResults(
  index: readonly PublicSearchEntry[],
  query: string,
  category: SearchCategory | 'All'
) {
  const normalized = normalizeSearch(query);
  const matching = normalized
    ? rankPublicSearch(index, normalized, category)
    : category === 'All'
      ? popularSearchPaths.flatMap((path) => {
          const item = index.find((i) => i.path === path);
          return item ? [{ ...item, score: 0 }] : [];
        })
      : index.filter((item) => item.category === category).map((item) => ({ ...item, score: 0 }));
  const limited = matching.slice(0, normalized ? 8 : 6);
  // Order groups by their best ranked match, then preserve ranking inside each group.
  const groups = [...new Set(limited.map((item) => item.category))];
  return {
    total: matching.length,
    items: groups.flatMap((group) => limited.filter((item) => item.category === group)),
  };
}
